/**
 * Responsibility: turn the hub's Decimal-as-string money into display text.
 *
 * The hub sends prices as strings on purpose - a JSON number is a float, and
 * these are prices. So the GUI has to parse before it can format, and doing
 * that in one place is what stops a `Number()` call appearing in a cell where
 * a null would quietly become `$0.00`.
 *
 * Absent stays absent. The kit's house rule is that missing data renders as an
 * em dash rather than a zero, and on this page the distinction matters more
 * than usual: "no PSA 10 has sold" and "a PSA 10 sells for nothing" are very
 * different statements about a card.
 */
import { EMPTY, fmtCurrency, fmtPercent } from '@kit/utils';

/** A decimal string as a number, or null if it is absent or unparseable. */
export function toNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Money from a decimal string. */
export function money(value: string | null | undefined): string {
  return fmtCurrency(toNumber(value));
}

/** Money with an explicit sign, for a profit that can go either way. */
export function signedMoney(value: string | null | undefined): string {
  const parsed = toNumber(value);
  if (parsed === null) return EMPTY;
  return `${parsed >= 0 ? '+' : '-'}${fmtCurrency(Math.abs(parsed))}`;
}

/** A ratio as a multiple, e.g. "6.7x". */
export function multiple(value: string | null | undefined): string {
  const parsed = toNumber(value);
  return parsed === null ? EMPTY : `${parsed.toFixed(1)}x`;
}

/** A fraction from a decimal string, as a percentage. */
export function percent(value: string | null | undefined, decimals = 1): string {
  return fmtPercent(toNumber(value), decimals);
}

/**
 * A trend ratio as the change it represents: 0.67 becomes "down 33%".
 *
 * Phrased in words rather than shown as a signed percentage because the sign
 * of a ratio below one is the thing readers get backwards, and this page uses
 * it to warn someone off spending money.
 */
export function trend(value: string | null | undefined): string {
  const parsed = toNumber(value);
  if (parsed === null) return 'not enough sales to say';
  const change = Math.round(Math.abs(1 - parsed) * 100);
  if (change < 2) return 'holding steady';
  return parsed < 1 ? `down ${change}%` : `up ${change}%`;
}

/**
 * Money shortened to fit a narrow column: 1553.88 becomes "$1.53K".
 *
 * Only for table cells, which are the place a four-figure price wraps onto a
 * second line and knocks the row out of alignment. The dialog shows the exact
 * figure, because that is where someone is deciding what to spend.
 */
export function compactMoney(value: string | null | undefined): string {
  const parsed = toNumber(value);
  if (parsed === null) return EMPTY;

  const sign = parsed < 0 ? '-' : '';
  const size = Math.abs(parsed);
  if (size >= 1_000_000) return `${sign}$${(size / 1_000_000).toFixed(2)}M`;
  if (size >= 1_000) return `${sign}$${(size / 1_000).toFixed(2)}K`;
  // Under a thousand the exact figure already fits, and rounding a $24.99
  // buy price to "$25" would misstate the one number with a hard ceiling.
  return fmtCurrency(parsed);
}

/** Compact money with an explicit sign, for a profit that can go either way. */
export function compactSigned(value: string | null | undefined): string {
  const parsed = toNumber(value);
  if (parsed === null) return EMPTY;
  return `${parsed >= 0 ? '+' : '-'}${compactMoney(String(Math.abs(parsed)))}`;
}
